import { useState } from 'react'
import { C } from '@/constants/theme'

// ── Logo ─────────────────────────────────────────────────────────────────────
interface LogoProps { size?: number; showText?: boolean }
export function Logo({ size = 40, showText = true }: LogoProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg width={size} height={size * 0.6} viewBox="0 0 120 72" fill="none">
        <ellipse cx="42" cy="36" rx="38" ry="28" stroke="#3D3D3D" strokeWidth="2.5" fill="none" />
        <path d="M20 28 Q42 8 80 20 Q100 24 108 36" stroke="#3D3D3D" strokeWidth="2.5" fill="none" />
        <path d="M42 8 L85 18 Q108 26 110 36 Q108 48 85 52 L42 62" stroke="#3D3D3D" strokeWidth="1.5" fill="none" opacity="0.4" />
        <path d="M48 12 Q72 10 92 22 Q104 30 106 36" stroke={C.blue} strokeWidth="3" fill="none" />
        <path d="M108 36 L118 36" stroke="#3D3D3D" strokeWidth="1.5" />
        <path d="M112 34 L118 36 L112 38" fill="#3D3D3D" />
        <ellipse cx="52" cy="28" rx="16" ry="10" fill={C.blue} opacity="0.2" />
        <path d="M4 42 L18 50" stroke="#3D3D3D" strokeWidth="1.5" opacity="0.3" />
      </svg>
      {showText && (
        <div style={{ lineHeight: 1, fontFamily: "'Rajdhani', sans-serif" }}>
          <span style={{ fontSize: size * 0.45, fontWeight: 700, color: '#3D3D3D', letterSpacing: 1 }}>GLASS</span>
          <span style={{ fontSize: size * 0.45, fontWeight: 700, color: C.blue, letterSpacing: 1 }}>TECH</span>
          <div style={{ fontSize: size * 0.18, color: C.textLight, letterSpacing: 3, marginTop: -2 }}>PTY LTD</div>
        </div>
      )}
    </div>
  )
}

// ── Chevrons ──────────────────────────────────────────────────────────────────
export function Chevrons({ opacity = 1 }: { opacity?: number }) {
  return (
    <div style={{ display: 'flex', gap: 5, opacity, alignItems: 'center' }}>
      <div style={{ width: 32, height: 10, background: C.blue, clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%, 15% 50%)' }} />
      <div style={{ width: 16, height: 10, background: C.blue, opacity: 0.4, clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%, 15% 50%)' }} />
      <div style={{ width: 10, height: 10, background: C.blue, opacity: 0.15, clipPath: 'polygon(0 0, 85% 0, 100% 50%, 85% 100%, 0 100%, 15% 50%)' }} />
    </div>
  )
}

// ── Button ────────────────────────────────────────────────────────────────────
interface BtnProps {
  children: React.ReactNode
  onClick?: () => void
  outline?: boolean
  size?: 'sm' | 'md'
  type?: 'button' | 'submit'
  disabled?: boolean
}
export function Btn({ children, onClick, outline = false, size = 'md', type = 'button', disabled = false }: BtnProps) {
  const [hov, setHov] = useState(false)
  const pad = size === 'sm' ? '8px 18px' : '13px 30px'
  const fs  = size === 'sm' ? 12 : 14
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: pad, fontSize: fs,
        fontFamily: "'Rajdhani', sans-serif", fontWeight: 700,
        letterSpacing: 1.5, textTransform: 'uppercase',
        background: disabled ? C.grayMid : outline ? (hov ? C.blueLight : 'transparent') : (hov ? C.blueDark : C.blue),
        color: disabled ? C.textLight : outline ? C.blue : C.white,
        border: outline ? `2px solid ${C.blue}` : 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        boxShadow: !outline && !disabled ? (hov ? `0 6px 20px rgba(0,174,239,0.35)` : `0 2px 10px rgba(0,174,239,0.2)`) : 'none',
      }}
    >
      {children}
    </button>
  )
}

// ── Section Label ─────────────────────────────────────────────────────────────
export function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <Chevrons opacity={0.7} />
      <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 11, letterSpacing: 4, color: C.blue, textTransform: 'uppercase' }}>{text}</span>
    </div>
  )
}

// ── Section Title ─────────────────────────────────────────────────────────────
interface SectionTitleProps {
  label?: string
  title: string
  sub?: string
  center?: boolean
}
export function SectionTitle({ label, title, sub, center = true }: SectionTitleProps) {
  return (
    <div style={{ marginBottom: 48, textAlign: center ? 'center' : 'left' }}>
      {label && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: center ? 'center' : 'flex-start', gap: 10, marginBottom: 12 }}>
          <Chevrons opacity={0.7} />
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 11, letterSpacing: 4, color: C.blue, textTransform: 'uppercase' }}>{label}</span>
        </div>
      )}
      <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 700, color: C.text, margin: 0, lineHeight: 1.1 }}>{title}</h2>
      {sub && <p style={{ color: C.textMid, marginTop: 14, fontSize: 15, maxWidth: 560, margin: center ? '14px auto 0' : '14px 0 0', lineHeight: 1.7 }}>{sub}</p>}
    </div>
  )
}

// ── Page Header ───────────────────────────────────────────────────────────────
interface PageHeaderProps { label: string; title: string }
export function PageHeader({ label, title }: PageHeaderProps) {
  return (
    <div style={{ background: 'linear-gradient(135deg, #F0F9FF, #F5F7FA)', padding: '60px 24px', borderBottom: `1px solid ${C.grayBorder}` }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <SectionLabel text={label} />
        <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(32px, 5vw, 56px)', fontWeight: 700, color: C.text, margin: 0 }}>{title}</h1>
      </div>
    </div>
  )
}

// ── FormField ─────────────────────────────────────────────────────────────────
interface FormFieldProps {
  label: string
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
  options?: string[]
  rows?: number
}
export function FormField({ label, value, onChange, type = 'text', placeholder = '', options, rows }: FormFieldProps) {
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 14px',
    background: C.grayLight, border: `1px solid ${C.grayBorder}`,
    color: C.text, fontFamily: "'Rajdhani', sans-serif",
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
  }
  const labelEl = (
    <label style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 10, letterSpacing: 2, color: C.textLight, textTransform: 'uppercase', display: 'block', marginBottom: 5 }}>{label}</label>
  )
  if (options) return (
    <div style={{ marginBottom: 18 }}>
      {labelEl}
      <select value={value} onChange={e => onChange(e.target.value)} style={inputStyle}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  )
  if (rows) return (
    <div style={{ marginBottom: 18 }}>
      {labelEl}
      <textarea rows={rows} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, resize: 'vertical' }} />
    </div>
  )
  return (
    <div style={{ marginBottom: 18 }}>
      {labelEl}
      <input type={type} placeholder={placeholder} value={value} onChange={e => onChange(e.target.value)} style={inputStyle} />
    </div>
  )
}
